/**
 * Melee Combat Interceptor (Phase 3)
 *
 * Replaces the generic CombatService with the canonical MDL melee engine
 * for all Dungeo villains (thief, troll, cyclops).
 *
 * Registered on CombatantTrait for if.action.attacking.
 *
 * Flow:
 * 1. preValidate: Check if hero is staggered (block attack if so)
 * 2. postExecute: Run melee engine instead of CombatService
 *    - Resolve blow using canonical tables
 *    - Apply wound/stagger/weapon-loss side effects
 *    - Populate sharedData with melee results
 * 3. postReport: Emit melee-specific combat message
 */

import {
  ActionInterceptor,
  InterceptorSharedData,
  InterceptorResult,
  IFEntity,
  WorldModel,
  IdentityTrait,
  TraitType,
  NpcTrait,
  CombatantTrait,
  RoomBehavior,
  RoomTrait,
  Direction,
  createEffect,
  CapabilityEffect,
} from '@sharpee/world-model';
import { createSeededRandom, SeededRandom } from '@sharpee/core';


/**
 * Module-level random instance shared across all melee calls.
 * Creating a new SeededRandom per attack causes identical rolls when
 * multiple attacks execute within the same Date.now() millisecond
 * (e.g., transcript tester running 30 attacks in rapid succession).
 */
const meleeRandom: SeededRandom = createSeededRandom();

import {
  fightStrength,
  villainStrength,
  getBestWeaponPenalty,
  resolveBlow,
  applyVillainBlowToHero,
  isHeroDeadFromWounds,
  MeleeOutcome,
} from '../combat/melee';
import type { BlowResult } from '../combat/melee';
import {
  getHeroAttackMessage,
  getVillainAttackMessage,
  MeleeMessages,
} from '../combat/melee-messages';
import { MELEE_STATE, getBaseOstrength } from '../combat/melee-state';
import { createEmptyFrame } from '../objects/thiefs-canvas-objects';

// Troll knockout constants (from MDL act1.254, dung.355)
const TROLL_UNCONSCIOUS_DESC = 'An unconscious troll is sprawled on the floor. All passages out of the room are open.';
const TROLL_RECOVERY_TURNS = 4;

/**
 * Get the villain key for message lookup.
 */
function getVillainKey(villain: IFEntity): string {
  const identity = villain.get(TraitType.IDENTITY) as IdentityTrait | undefined;
  const name = identity?.name?.toLowerCase() ?? '';
  if (name.includes('troll')) return 'troll';
  if (name.includes('thief')) return 'thief';
  if (name.includes('cyclops')) return 'cyclops';
  return 'troll'; // Default
}

/**
 * Get the villain's display name for messages (e.g., "troll", "thief").
 */
function getVillainDisplayName(villain: IFEntity): string {
  const key = getVillainKey(villain);
  // Use the key directly — matches how MDL uses D placeholder
  return key;
}

/**
 * Get the hero's weapon type for message lookup ('sword' or 'knife').
 */
function getWeaponType(weaponName: string | undefined): string {
  if (!weaponName) return 'sword';
  const lower = weaponName.toLowerCase();
  if (lower.includes('sword')) return 'sword';
  return 'knife'; // knife, stiletto, nasty knife, etc.
}

/**
 * Get current player score from the score ledger.
 */
function getPlayerScore(world: WorldModel): number {
  return world.getScore();
}

/**
 * Get or initialize melee state for a villain.
 */
function getVillainOstrength(villain: IFEntity): number {
  const stored = villain.attributes[MELEE_STATE.VILLAIN_OSTRENGTH];
  if (typeof stored === 'number') return stored;
  // Initialize from canonical values
  const base = getBaseOstrength(villain);
  villain.attributes[MELEE_STATE.VILLAIN_OSTRENGTH] = base;
  return base;
}

/**
 * Handle villain-specific knockout side effects.
 *
 * Called when a villain is knocked unconscious. Applies villain-specific
 * mutations (description, exit unblocking, recovery turns) that were
 * previously handled by entity `on` handlers (ISSUE-068).
 */
function handleVillainKnockout(
  villainKey: string,
  villain: IFEntity,
  world: WorldModel
): void {
  switch (villainKey) {
    case 'troll': {
      // Update description to TROLLOUT (MDL act1.254)
      const identity = villain.get(TraitType.IDENTITY) as IdentityTrait | undefined;
      if (identity) {
        identity.description = TROLL_UNCONSCIOUS_DESC;
      }

      // Unblock north exit — troll no longer blocks passage
      const villainRoomId = world.getLocation(villain.id);
      const room = villainRoomId ? world.getEntity(villainRoomId) : undefined;
      if (room) {
        RoomBehavior.unblockExit(room, Direction.NORTH);
      }

      // Set recovery turns for the troll recovery daemon
      const combatant = villain.get(TraitType.COMBATANT) as CombatantTrait | undefined;
      if (combatant) {
        combatant.recoveryTurns = TROLL_RECOVERY_TURNS;
      }
      break;
    }
  }
}

/**
 * Handle villain-specific death side effects.
 *
 * Events in Sharpee are messages (not pub/sub), so entity `.on` handlers
 * don't fire automatically. Death side effects must happen in the
 * execution flow, not in event handlers.
 *
 * @returns true if entity cleanup was handled (skip default inventory drop)
 */
function handleVillainDeath(
  villainKey: string,
  villain: IFEntity,
  world: WorldModel,
  sharedData: InterceptorSharedData
): boolean {
  const villainRoomId = world.getLocation(villain.id);
  const room = villainRoomId ? world.getEntity(villainRoomId) : undefined;

  switch (villainKey) {
    case 'troll': {
      // Unblock north exit (troll was blocking passage)
      if (room) {
        RoomBehavior.unblockExit(room, Direction.NORTH);
      }
      // Remove troll and its weapon from the game (both disappear in smoke)
      // Note: No bonus points for killing the troll (not in FORTRAN source)
      const contents = world.getContents(villain.id);
      for (const item of contents) {
        world.removeEntity(item.id);
      }
      world.removeEntity(villain.id);

      // Store death messages for postReport to emit
      sharedData.deathMessages = [
        'Almost as soon as the troll breathes his last, a cloud of sinister black smoke envelops him, and when the fog lifts, the carcass has disappeared.',
        'With the troll dispatched, the passage to the north is now clear.',
      ];

      return true; // Entity cleanup handled — skip default inventory drop
    }

    case 'thief': {
      // Canonical MDL thief death (melee.mud:272-280, act1.mud:1272-1296)
      // 1. Drop all inventory to floor (except stiletto)
      const stilettoId = villain.attributes.stilettoId as string | undefined;
      const thiefContents = world.getContents(villain.id);
      let hasLoot = false;
      for (const item of thiefContents) {
        if (item.id === stilettoId) continue; // stiletto disappears with body
        world.moveEntity(item.id, villainRoomId ?? null);
        hasLoot = true;
      }

      // ADR-078: Hidden max points — canvas treasure becomes achievable
      // Note: No bonus points for killing the thief (not in FORTRAN source)
      world.setMaxScore(650);
      world.getDataStore().state['dungeo.thief.dead'] = true;
      world.getDataStore().state['dungeo.reality_altered_pending'] = true;

      // 3. Spawn empty frame in the Treasure Room (thief's lair)
      // The lair ID is stored in the thief's NPC custom properties
      const npcTrait = villain.get(NpcTrait);
      const storedLairId = npcTrait?.customProperties?.lairRoomId;
      const lairRoomId = typeof storedLairId === 'string' ? storedLairId : villainRoomId;
      const frame = createEmptyFrame(world);
      world.moveEntity(frame.id, lairRoomId ?? null);

      // 4. Reveal all concealed treasures in the lair (MDL: OVISON restored on death)
      if (lairRoomId) {
        const lairContents = world.getContents(lairRoomId);
        for (const item of lairContents) {
          const itemIdentity = item.get(TraitType.IDENTITY) as IdentityTrait | undefined;
          if (itemIdentity?.concealed) {
            itemIdentity.concealed = false;
          }
        }
      }

      // 5. Disable thief daemon permanently
      world.getDataStore().state['dungeo.thief.disabled'] = true;

      // 6. Remove stiletto and thief entity (carcass disappears in black fog)
      if (stilettoId) {
        world.removeEntity(stilettoId);
      }
      world.removeEntity(villain.id);

      // 7. Store death messages for postReport to emit
      const deathMessages: string[] = [];
      if (hasLoot) {
        deathMessages.push("The thief's ill-gotten gains scatter across the floor.");
      }
      deathMessages.push(
        'Almost as soon as the thief breathes his last breath, a cloud of sinister black fog envelops him, and when the fog lifts, the carcass has disappeared.'
      );
      sharedData.deathMessages = deathMessages;

      return true; // Entity cleanup handled
    }
  }
  return false; // Use default inventory drop
}

// ============= The Interceptor =============

export const MeleeInterceptor: ActionInterceptor = {
  /**
   * PRE-VALIDATE: Check if the hero is staggered (misses next turn).
   */
  preValidate(
    _entity: IFEntity,
    world: WorldModel,
    actorId: string,
    sharedData: InterceptorSharedData
  ): InterceptorResult | null {
    const player = world.getEntity(actorId);
    if (!player) return null;

    const staggered = player.attributes[MELEE_STATE.STAGGERED];
    if (staggered) {
      // Clear stagger — the missed turn is consumed
      player.attributes[MELEE_STATE.STAGGERED] = false;
      return {
        valid: false,
        error: MeleeMessages.STILL_RECOVERING,
      };
    }

    return null; // Continue with standard validation
  },

  /**
   * POST-EXECUTE: Replace CombatService with canonical melee engine.
   *
   * The attacking action calls this INSTEAD of CombatService when
   * an interceptor is present. We must populate sharedData with:
   * - attackResult: AttackResult compatible object
   * - customMessage: the melee message text
   * - usedCombatService: false
   */
  postExecute(
    villain: IFEntity,
    world: WorldModel,
    actorId: string,
    sharedData: InterceptorSharedData
  ): void {
    const player = world.getEntity(actorId);
    if (!player) return;

    const random = meleeRandom;
    const weaponName = sharedData.weaponName as string | undefined;
    const villainKey = getVillainKey(villain);
    const villainDisplay = getVillainDisplayName(villain);

    // --- Compute attacker/defender strengths ---
    const score = getPlayerScore(world);
    const woundAdjust = (player.attributes[MELEE_STATE.WOUND_ADJUST] as number) ?? 0;
    const heroStr = Math.max(1, fightStrength(score, woundAdjust));

    const currentOstrength = getVillainOstrength(villain);
    const isThiefEngrossed = villain.attributes.thiefEngrossed === true;
    const bestWeaponPenalty = getBestWeaponPenalty(villainKey, weaponName ?? '');
    const villainStr = villainStrength(
      currentOstrength,
      isThiefEngrossed,
      bestWeaponPenalty > 0,
      bestWeaponPenalty
    );

    // --- Check if villain is unconscious (auto-kill) ---
    const villainUnconscious = villain.attributes[MELEE_STATE.VILLAIN_UNCONSCIOUS] === true;

    // --- Resolve the hero's blow ---
    // Pass raw currentOstrength when unconscious (negative) so resolveBlow
    // triggers auto-kill (def < 0). villainStrength() clamps to 0 which
    // would cause resolveBlow to return MISSED instead.
    const defForBlow = villainUnconscious ? currentOstrength : villainStr;
    const blowResult = resolveBlow(heroStr, defForBlow, true, villainUnconscious, random);

    // --- Apply side effects ---
    let targetKilled = false;
    let targetKnockedOut = false;
    const droppedItems: string[] = [];

    switch (blowResult.outcome) {
      case MeleeOutcome.KILLED:
      case MeleeOutcome.SITTING_DUCK:
        targetKilled = true;
        villain.attributes[MELEE_STATE.VILLAIN_OSTRENGTH] = 0;
        villain.attributes[MELEE_STATE.VILLAIN_UNCONSCIOUS] = false;
        break;

      case MeleeOutcome.UNCONSCIOUS:
        targetKnockedOut = true;
        villain.attributes[MELEE_STATE.VILLAIN_UNCONSCIOUS] = true;
        villain.attributes[MELEE_STATE.VILLAIN_OSTRENGTH] = -currentOstrength;
        break;

      case MeleeOutcome.LIGHT_WOUND:
        villain.attributes[MELEE_STATE.VILLAIN_OSTRENGTH] = blowResult.newDefenderStrength;
        if (blowResult.defenderKilled) {
          targetKilled = true;
        }
        break;

      case MeleeOutcome.SERIOUS_WOUND:
        villain.attributes[MELEE_STATE.VILLAIN_OSTRENGTH] = blowResult.newDefenderStrength;
        if (blowResult.defenderKilled) {
          targetKilled = true;
        }
        break;

      case MeleeOutcome.STAGGER:
        villain.attributes[MELEE_STATE.VILLAIN_STAGGERED] = true;
        break;

      case MeleeOutcome.LOSE_WEAPON:
        // Drop villain's weapon to the floor
        {
          const villainContents = world.getContents(villain.id);
          const villainRoom = world.getLocation(villain.id) ?? null;
          for (const item of villainContents) {
            if (item.has(TraitType.WEAPON)) {
              world.moveEntity(item.id, villainRoom);
              droppedItems.push(item.id);
              break; // Only drop one weapon
            }
          }
        }
        break;
    }

    // Handle all kill outcomes uniformly (direct kills AND wound-kills)
    if (targetKilled) {
      const combatant = villain.get(TraitType.COMBATANT) as CombatantTrait | undefined;
      if (combatant) {
        // Direct assignment instead of combatant.kill() — after world.loadJSON()
        // traits are plain objects without methods, so kill() would crash.
        combatant.health = 0;
        combatant.isAlive = false;
        combatant.isConscious = false;
      }
      // Villain-specific death side effects (unblock exits, score, entity removal).
      // Returns true if the handler cleaned up entities (troll disappears in smoke).
      if (!handleVillainDeath(villainKey, villain, world, sharedData)) {
        // Default: drop villain's inventory to the floor
        const villainContents = world.getContents(villain.id);
        const villainRoom = world.getLocation(villain.id) ?? null;
        for (const item of villainContents) {
          world.moveEntity(item.id, villainRoom);
          droppedItems.push(item.id);
        }
      }
    }

    // Handle knockout outcomes (ISSUE-068: moved from entity `on` handler)
    if (targetKnockedOut) {
      const combatant = villain.get(TraitType.COMBATANT) as CombatantTrait | undefined;
      if (combatant) {
        // Direct assignment — same reason as kill path above
        combatant.isConscious = false;
      }
      // Sync NpcTrait consciousness (NPC service checks NpcTrait.canAct separately)
      const npcTrait = villain.get(NpcTrait);
      if (npcTrait) {
        npcTrait.isConscious = false;
      }
      // Villain-specific knockout side effects (description, exits, recovery)
      handleVillainKnockout(villainKey, villain, world);
    }

    // --- Get the combat message ---
    const weaponType = getWeaponType(weaponName);
    const message = getHeroAttackMessage(
      weaponType,
      blowResult.outcome,
      villainDisplay,
      (arr) => random.pick(arr)
    ) ?? 'You attack.';

    // --- Store results for report phase ---
    sharedData.attackResult = {
      success: true,
      type: targetKilled ? 'killed' :
            targetKnockedOut ? 'knocked_out' :
            blowResult.outcome === MeleeOutcome.MISSED ? 'missed' : 'hit',
      damage: 0,
      remainingHitPoints: getVillainOstrength(villain),
      targetDestroyed: false,
      targetKilled,
      targetKnockedOut,
      itemsDropped: droppedItems.length > 0 ? droppedItems : undefined,
    };
    sharedData.customMessage = MeleeMessages.HERO_ATTACK;
    sharedData.usedCombatService = false;

    // Store for postReport
    sharedData.meleeMessage = message;
    sharedData.meleeOutcome = blowResult.outcome;
    sharedData.meleeTargetKilled = targetKilled;
  },

  /**
   * POST-REPORT: Emit the canonical melee message as a game.message event.
   */
  postReport(
    _villain: IFEntity,
    _world: WorldModel,
    _actorId: string,
    sharedData: InterceptorSharedData
  ): CapabilityEffect[] {
    const effects: CapabilityEffect[] = [];

    const message = sharedData.meleeMessage as string | undefined;
    if (message) {
      effects.push(
        createEffect('game.message', {
          messageId: MeleeMessages.HERO_ATTACK,
          text: message,
        }),
      );
    }

    // Emit villain death messages (e.g., thief's "black fog" / "booty remains")
    const deathMessages = sharedData.deathMessages as string[] | undefined;
    if (deathMessages) {
      for (const text of deathMessages) {
        effects.push(createEffect('game.message', { text }));
      }
    }

    return effects;
  },

  /**
   * ON-BLOCKED: Custom message when hero is staggered.
   */
  onBlocked(
    _entity: IFEntity,
    _world: WorldModel,
    _actorId: string,
    error: string,
    _sharedData: InterceptorSharedData
  ): CapabilityEffect[] | null {
    if (error === MeleeMessages.STILL_RECOVERING) {
      return [
        createEffect('game.message', {
          messageId: MeleeMessages.STILL_RECOVERING,
          text: 'You are still recovering from a staggering blow.',
        }),
      ];
    }
    return null; // Use standard blocked handling
  },
};
